import api, { handleResponse, handleError } from './api';

const adminService = {
  async getUsers(page = 1, search = '', filter = '') {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (search) params.append('search', search);
      if (filter && filter !== 'all') params.append('filter', filter);
      const response = await api.get(`/admin/users?${params}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** MONEY › WALLETS: every wallet holding money, largest first; `all` widens it to everyone. */
  async getWallets(page = 1, { search = '', all = false, pageSize = 30 } = {}) {
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (search) params.append('search', search);
      if (all) params.append('all', 'true');
      const response = await api.get(`/admin/wallets?${params}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getUserDetail(userId) {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Superadmin-only: flag/unflag an account as a demo/test account (hidden from search). */
  async setTestAccount(userId, isTest) {
    try {
      const response = await api.put(`/admin/users/${userId}/test-account`, { isTest });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Set per-host Portal service fee override.
   *  cents: null = use global default ($1); 0 = waive entirely; >0 = custom cents.
   *  The Stripe processing fee is NEVER affected — it always applies. */
  async setPrFeeOverride(userId, cents) {
    try {
      const response = await api.put(`/admin/users/${userId}/pr-fee-override`, { cents });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteUser(userId) {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async verifyUser(userId) {
    try {
      const response = await api.put(`/admin/users/${userId}/verify`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** A signed `amount` in cents, or `setBalanceTo` (the exact available balance) — reason required. */
  async adjustWallet(userId, amount, reason, setBalanceTo = null) {
    try {
      const body = setBalanceTo == null ? { amount, reason } : { amount: 0, setBalanceTo, reason };
      const response = await api.post(`/admin/users/${userId}/wallet-adjustment`, body);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Superadmin: available balance from `userId` to `toUserId`. */
  async transferWallet(userId, toUserId, amount, reason) {
    try {
      const response = await api.post(`/admin/users/${userId}/wallet-transfer`, { toUserId, amount, reason });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** The transfer's person picker (superadmin). */
  async searchWalletPeople(q, exclude) {
    try {
      const response = await api.get('/admin/wallets/people', { params: { q, exclude } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getTransactions(page = 1, pageSize = 30, filters = {}) {
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.type) params.append('type', filters.type);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.source) params.append('source', filters.source);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      const response = await api.get(`/admin/transactions?${params}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getEvents(page = 1, filters = {}) {
    try {
      const params = new URLSearchParams({ page, pageSize: filters.pageSize || 20 });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      // The AREA bar's city + radius, the date window, and the sort.
      if (Number.isFinite(filters.lat) && Number.isFinite(filters.lng)) {
        params.append('lat', filters.lat);
        params.append('lng', filters.lng);
        params.append('radiusMiles', filters.radiusMiles ?? 50);
      }
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.when) params.append('when', filters.when);
      if (filters.sort) params.append('sort', filters.sort);
      const response = await api.get(`/admin/events?${params}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getEventDetail(eventId) {
    try {
      const response = await api.get(`/admin/events/${eventId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── EVENTS › GATHERINGS (capability gatherings) ──
  async getGatherings(page = 1, filters = {}) {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (filters.status) params.append('status', filters.status);
      if (filters.entry) params.append('entry', filters.entry);
      if (filters.search) params.append('search', filters.search);
      return handleResponse(await api.get(`/admin/gatherings?${params}`));
    } catch (error) { return handleError(error); }
  },
  async getGatheringDetail(id) {
    try { return handleResponse(await api.get(`/admin/gatherings/${id}`)); } catch (error) { return handleError(error); }
  },
  async cancelGathering(id, reason) {
    try { return handleResponse(await api.put(`/admin/gatherings/${id}/cancel`, { reason })); } catch (error) { return handleError(error); }
  },

  async cancelEvent(eventId, reason) {
    try {
      const response = await api.put(`/admin/events/${eventId}/cancel`, { reason });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getWithdrawals(page = 1, status = '') {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (status) params.append('status', status);
      const response = await api.get(`/admin/withdrawals?${params}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** Staff (withdrawals · approve): re-run the Stripe transfer for a failed withdrawal. */
  async retryWithdrawal(id) {
    try { return handleResponse(await api.post(`/admin/withdrawals/${id}/retry`)); } catch (error) { return handleError(error); }
  },

  /** Staff (withdrawals · approve): mark a stuck withdrawal failed and refund the wallet. */
  async markWithdrawalFailed(id, reason, confirmNoProviderTransfer = false) {
    try { return handleResponse(await api.post(`/admin/withdrawals/${id}/mark-failed`, { reason, confirmNoProviderTransfer })); } catch (error) { return handleError(error); }
  },

  /** Staff (withdrawals · approve): complete a processing withdrawal from the transfer Stripe made
   *  (found by the withdrawal's id at Stripe when `transferId` is empty). No money moves. */
  async markWithdrawalCompleted(id, transferId = null) {
    try { return handleResponse(await api.post(`/admin/withdrawals/${id}/mark-completed`, { transferId })); } catch (error) { return handleError(error); }
  },

  /** CITY PORTALS (squares · view). */
  async getSquares({ q = '', take = 200 } = {}) {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('take', String(take));
      return handleResponse(await api.get(`/admin/squares?${params.toString()}`));
    } catch (error) { return handleError(error); }
  },

  /** PLAN DETECTION (plan_detection · view): the funnel, every read, the accounts, the live scorer. */
  async getUserReports(status = 'waiting', page = 1) {
    try { return handleResponse(await api.get(`/admin/user-reports?status=${status}&page=${page}`)); } catch (error) { return handleError(error); }
  },
  async getSquareReports() {
    try { return handleResponse(await api.get('/admin/user-reports/square')); } catch (error) { return handleError(error); }
  },
  async updateUserReport(id, { status, note }) {
    try { return handleResponse(await api.put(`/admin/user-reports/${id}`, { status, note })); } catch (error) { return handleError(error); }
  },
  async takeDownReported(id) {
    try { return handleResponse(await api.post(`/admin/user-reports/${id}/take-down`)); } catch (error) { return handleError(error); }
  },
  async getPlanDetection() {
    try { return handleResponse(await api.get('/admin/plan-detection')); } catch (error) { return handleError(error); }
  },
  async getPlanReads({ page = 1, pageSize = 25, outcome = null, userId = null, callsOnly = false } = {}) {
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (outcome) params.set('outcome', outcome);
      if (userId) params.set('userId', userId);
      if (callsOnly) params.set('callsOnly', 'true');
      return handleResponse(await api.get(`/admin/plan-detection/reads?${params.toString()}`));
    } catch (error) { return handleError(error); }
  },
  async getPlanAccounts(page = 1, pageSize = 25) {
    try { return handleResponse(await api.get(`/admin/plan-detection/accounts?page=${page}&pageSize=${pageSize}`)); } catch (error) { return handleError(error); }
  },
  async scorePlanText(text) {
    try { return handleResponse(await api.post('/admin/plan-detection/score', { text })); } catch (error) { return handleError(error); }
  },

  /** PLANNER (planner · view). */
  async getPlans(page = 1, pageSize = 20) {
    try { return handleResponse(await api.get(`/admin/plans?page=${page}&pageSize=${pageSize}`)); } catch (error) { return handleError(error); }
  },

  /** PEOPLE › HOSTS (hosts · view). `filter` = all | live | holding | instant | custom. */
  async getHosts(page = 1, search = '', filter = '') {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (search) params.append('search', search);
      if (filter && filter !== 'all') params.append('filter', filter);
      return handleResponse(await api.get(`/admin/hosts?${params}`));
    } catch (error) { return handleError(error); }
  },

  /** The tiles and charts at the top of HOSTS — hosts only. */
  async getHostStats(days = 30) {
    try { return handleResponse(await api.get('/admin/hosts/stats', { params: { days } })); } catch (error) { return handleError(error); }
  },

  /** The dropdown under a host: wallet, hosting record, what is held and why. */
  async getHostDetail(userId) {
    try { return handleResponse(await api.get(`/admin/hosts/${userId}`)); } catch (error) { return handleError(error); }
  },

  /** INSTANT ESCROW + RELEASE DELAY (hosts · edit). `escrowReleaseHours` null = the default 24. */
  async setHostEscrowSettings(userId, { instantEscrow, escrowReleaseHours }) {
    try { return handleResponse(await api.put(`/admin/hosts/${userId}/escrow-settings`, { instantEscrow, escrowReleaseHours })); } catch (error) { return handleError(error); }
  },

  /** RELEASE NOW (hosts · release): the picked events' positions, with the step log. No ids = every event. */
  async releaseHostEscrow(userId, eventIds = null) {
    try { return handleResponse(await api.post(`/admin/hosts/${userId}/release-escrow`, { eventIds: eventIds || [] })); } catch (error) { return handleError(error); }
  },

  async getStats() {
    try {
      const response = await api.get('/admin/stats');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** The breakdown behind every STATS tile (stats · view). A second call on purpose. */
  async getStatBreakdowns() {
    try { return handleResponse(await api.get('/admin/stats/breakdowns')); } catch (error) { return handleError(error); }
  },

  /** MONEY IN per day, by city, with the events behind each day. */
  async getRevenue(days = 30) {
    try { return handleResponse(await api.get('/admin/stats/revenue', { params: { days } })); } catch (error) { return handleError(error); }
  },

  /** NEW ACCOUNTS per day, by how they arrived, with the people behind each day. */
  async getSignups(days = 30) {
    try { return handleResponse(await api.get('/admin/stats/signups', { params: { days } })); } catch (error) { return handleError(error); }
  },

  /** ESCROW BY EVENT, with who put the money in. */
  async getEscrowByEvent(take = 20) {
    try { return handleResponse(await api.get('/admin/stats/escrow', { params: { take } })); } catch (error) { return handleError(error); }
  },

  /** FEES BY SOURCE. `days = 0` is all time. */
  async getFeesBySource(days = 0) {
    try { return handleResponse(await api.get('/admin/stats/fees', { params: { days } })); } catch (error) { return handleError(error); }
  },

  async getFeatureFlags() {
    try {
      const response = await api.get('/admin/feature-flags');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Pro pages ────────────────────────────────────────────────────────────
  async getPros({ q = '', filter = 'all', take = 100 } = {}) {
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (filter && filter !== 'all') params.set('filter', filter);
      params.set('take', String(take));
      return handleResponse(await api.get(`/admin/pros?${params.toString()}`));
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Invite cards (Oct 2026): a person's or a Pro page's referral code + the two print PNGs ──
  inviteCardBase(kind, id) { return `/admin/${kind === 'pro' ? 'pros' : 'users'}/${id}/invite-card`; },
  async mintInviteCard(kind, id) {
    try { return handleResponse(await api.post(this.inviteCardBase(kind, id))); } catch (error) { return handleError(error); }
  },
  async rotateInviteCard(kind, id) {
    try { return handleResponse(await api.post(`${this.inviteCardBase(kind, id)}/rotate`)); } catch (error) { return handleError(error); }
  },
  /** The PNG as a Blob — `side` is front | back; `preview` asks for the 1050 × 600 version. */
  async inviteCardImage(kind, id, side, preview = false) {
    try {
      const res = await api.get(`${this.inviteCardBase(kind, id)}/${side}.png${preview ? '?size=preview' : ''}`, { responseType: 'blob' });
      return { success: true, data: res.data };
    } catch (error) { return handleError(error); }
  },

  async setProPayoutTerms(id, { instantPayout, escrowHoldHours }) {
    try {
      return handleResponse(await api.put(`/admin/pros/${id}/payout-terms`, { instantPayout, escrowHoldHours }));
    } catch (error) {
      return handleError(error);
    }
  },

  async setProPublished(id, published) {
    try {
      return handleResponse(await api.put(`/admin/pros/${id}/published`, { published }));
    } catch (error) {
      return handleError(error);
    }
  },

  async deletePro(id) {
    try {
      return handleResponse(await api.delete(`/admin/pros/${id}`));
    } catch (error) {
      return handleError(error);
    }
  },

  async getSettings() {
    try {
      const response = await api.get('/admin/settings');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async updateSetting(key, value) {
    try {
      const response = await api.put(`/admin/settings/${encodeURIComponent(key)}`, { value });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ── Booking fees (fees · view / edit) — the four pro_*_fee_* keys live here, not in SETTINGS.
  async getFeeSettings() {
    try { return handleResponse(await api.get('/admin/settings/fees')); } catch (error) { return handleError(error); }
  },

  async updateFeeSetting(key, value) {
    try { return handleResponse(await api.put(`/admin/settings/fees/${encodeURIComponent(key)}`, { value })); } catch (error) { return handleError(error); }
  },
};

export default adminService;
